import React, { useState, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext } from '@contexts/AuthContext';
import NotFoundPage from '@pages/not-found-page';

const Email2FAVerificationPage = () => {
  const { uuid: uuidFromParams } = useParams<{ uuid: string }>();
  const authContext = useContext(AuthContext);
  const [securityCode, setSecurityCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const storedUuidRef = useRef(localStorage.getItem('uuid'));
  const storedUuid = storedUuidRef.current;

  if (!authContext) {
    throw new Error('AuthContext must be used within an AuthProvider');
  }

  const isInvalidPage =
    !loading &&
    (!uuidFromParams || !storedUuid || uuidFromParams !== storedUuid);

  if (isInvalidPage) {
    return <NotFoundPage />;
  }
  const { verifyOtp } = authContext;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedCode = securityCode.trim();

    if (!uuidFromParams) {
      setError('Verification session expired. Please login again.');
      return;
    }
    if (!trimmedCode) {
      setError('Please enter the 6 digit code');
      return;
    }

    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Please enter a valid 6 digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await verifyOtp(uuidFromParams, trimmedCode);
    } catch (err: any) {
      const message =
        err?.response?.data?.user_message ||
        err?.response?.data?.message ||
        'The code is invalid or has expired. Please try again.';

      setError(message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="govuk-width-container mt-5 mb-5 md:mb-10">
      <h1 className="govuk-heading-l">Check your email address</h1>
      <div className="govuk-inset-text">
        If you have a MyWishes Care account linked to your email address, we
        have sent a code to you. If you cannot locate the email please check
        your SPAM folder.
      </div>
      <p className="govuk-body">
        It might take a few minutes to arrive. The code will expire in 15
        minutes.
      </p>
      <form onSubmit={handleSubmit}>
        {error && (
          <div
            className="govuk-error-summary"
            data-module="govuk-error-summary"
          >
            <div role="alert">
              <h2 className="govuk-error-summary__title">There is a problem</h2>
              <div className="govuk-error-summary__body">
                <ul className="govuk-list govuk-error-summary__list">
                  <li>
                    <a href="#security-code">{error}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="security-code">
            Enter the 6 digital code
          </label>
          <input
            className="govuk-input govuk-input--width-4 govuk-input--extra-letter-spacing"
            id="security-code"
            name="securityCode"
            type="text"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            value={securityCode}
            onChange={e => {
              const onlyNumbers = e.target.value.replace(/\D/g, '');
              setSecurityCode(onlyNumbers);
            }}
          />
        </div>
        <button
          type="submit"
          className="govuk-button"
          data-module="govuk-button"
          disabled={loading}
        >
          Continue
        </button>
      </form>
      <details className="govuk-details">
        <summary className="govuk-details__summary">
          <span className="govuk-details__summary-text">
            You did not receive a code, what to do next?
          </span>
        </summary>
        <div className="govuk-details__text">
          <div className="govuk-body">
            <ul className="govuk-list govuk-list--bullet">
              <li>
                Please make sure that you entered the correct email you used
                when you first set up your account.
              </li>
              <li>
                If you cannot remember your password, please{' '}
                <a href="/forgot-password" className="govuk-link">
                  reset your password.
                </a>{' '}
                Once your password has been reset, please try to login once
                again.
              </li>
              <li>
                If you organisation does not currently use MyWishes Care, you
                will not be able to login. Please request that they contact us
                to be setup with their own environment.
              </li>
              <li>
                If you used to work for an organisation and they have removed
                you from their organisation, you will not be able to login to
                their environment. You may however still have access to MyWishes
                Care for other organisations that you work for. If this is the
                case and you cannot access your account,{' '}
                <a href="https://mywishes.care/contact/" className="govuk-link">
                  contact us.
                </a>{' '}
              </li>
            </ul>
          </div>
        </div>
      </details>
    </div>
  );
};

export default Email2FAVerificationPage;
